/**
 * Saved report definitions — a named report a person can open, change, and
 * save again, with an immutable version history behind it.
 *
 * The API serves camelCase already (it shares the pipeline saved-view
 * serializer), so nothing is transformed here.
 */
import api from './api'

export type ReportTypeId =
  | 'pipeline'
  | 'dossier'
  | 'committee_book'
  | 'screener'
  | 'asset_management'
  | 'pipeline_changes'

export type ReportDefinitionVersion = {
  id: string
  version: number
  reportType: ReportTypeId
  authorEmail: string
  note: string
  columns: string[]
  filters: Record<string, unknown>
  createdAt: string | null
}

export type ReportDefinition = {
  id: string
  workspaceId: string
  name: string
  description: string
  status: string
  reportType: ReportTypeId
  ownerEmail: string
  ownerUserId: string | null
  version: number
  columns: string[]
  filters: Record<string, unknown>
  isShared: boolean
  isSystem: boolean
  editors: string[]
  viewers: string[]
  createdAt: string | null
  updatedAt: string | null
  versions?: ReportDefinitionVersion[]
}

export type ReportDefinitionCreate = {
  name: string
  reportType: ReportTypeId
  description?: string
  columns?: string[]
  filters?: Record<string, unknown>
  isShared?: boolean
  note?: string
}

export type ReportDefinitionPatch = {
  name?: string
  description?: string
  columns?: string[]
  filters?: Record<string, unknown>
  isShared?: boolean
  /** What this save is called in the history. */
  note?: string
}

const BASE = '/reports/definitions'

export const reportDefinitionsService = {
  list: (reportType?: ReportTypeId) =>
    api
      .get<ReportDefinition[]>(BASE + '/', {
        params: reportType ? { report_type: reportType } : undefined,
      })
      .then((r) => r.data),

  /** The report itself, including the full version history for this id.
   *  The list is not a substitute: an admin listing every workspace report
   *  must still see the same snapshots the owner sees for this id. */
  get: (id: string) => api.get<ReportDefinition>(`${BASE}/${id}`).then((r) => r.data),

  create: (body: ReportDefinitionCreate) =>
    api.post<ReportDefinition>(BASE + '/', body).then((r) => r.data),

  update: (id: string, body: ReportDefinitionPatch) =>
    api.put<ReportDefinition>(`${BASE}/${id}`, body).then((r) => r.data),

  restore: (id: string, version: number) =>
    api.post<ReportDefinition>(`${BASE}/${id}/restore/${version}`).then((r) => r.data),

  duplicate: (id: string) =>
    api.post<ReportDefinition>(`${BASE}/${id}/duplicate`).then((r) => r.data),

  remove: (id: string) => api.delete(`${BASE}/${id}`).then(() => undefined),
}
